<template>
  <Teleport to="body">
    <div v-if="url" class="game-iframe-overlay">
      <iframe
        :src="url"
        class="game-iframe"
        frameborder="0"
        allow="autoplay; fullscreen; web-share *; clipboard-write *; screen-wake-lock *"
        allowfullscreen
      />
      <!-- 可拖动的退出 FAB (红底白字圆形) -->
      <button
        class="exit-fab"
        :class="{ dragging }"
        :style="{ left: pos.x + 'px', top: pos.y + 'px' }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
      >
        {{ closeText || '退出' }}
      </button>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * 游戏全屏 iframe 覆盖层 (统一首页/游戏列表/收藏页等所有启动游戏入口)
 * 配合 useGamePlay(): url=iframeUrl, @close=closeGame
 *
 * 退出按钮做成可拖动的红色圆形 FAB:
 *   - pointer 事件 + setPointerCapture → 拖动经过 iframe 时事件不被 iframe 抢走
 *   - 移动超过阈值算"拖动"(不触发关闭), 否则算"点击"(触发 close)
 *   - 位置 clamp 在视口内, 记住上次拖动位置
 */
import { ref, watch } from 'vue'

const props = defineProps<{
  url: string
  closeText?: string
}>()
const emit = defineEmits<{ (e: 'close'): void }>()

const FAB = 52 // 按钮直径
const MARGIN = 12 // 离边距

function defaultPos() {
  const w = typeof window !== 'undefined' ? window.innerWidth : 375
  return { x: w - FAB - MARGIN, y: 70 }
}

const pos = ref(defaultPos())
const dragging = ref(false)

let startX = 0
let startY = 0
let origX = 0
let origY = 0
let moved = false

function clampX(x: number) {
  const max = window.innerWidth - FAB - MARGIN
  return Math.max(MARGIN, Math.min(x, max))
}
function clampY(y: number) {
  const max = window.innerHeight - FAB - MARGIN
  return Math.max(MARGIN, Math.min(y, max))
}

function onPointerDown(e: PointerEvent) {
  dragging.value = true
  moved = false
  startX = e.clientX
  startY = e.clientY
  origX = pos.value.x
  origY = pos.value.y
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
}
function onPointerMove(e: PointerEvent) {
  if (!dragging.value) return
  const dx = e.clientX - startX
  const dy = e.clientY - startY
  if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved = true
  pos.value = { x: clampX(origX + dx), y: clampY(origY + dy) }
}
function onPointerUp(e: PointerEvent) {
  if (!dragging.value) return
  dragging.value = false
  ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
  // 没有移动 → 视为点击, 触发关闭; 有移动 → 松手后吸附到左右最近的一边 (Y 不动, 只吸 X)
  if (!moved) {
    emit('close')
  } else {
    const centerX = pos.value.x + FAB / 2
    const snapToLeft = centerX < window.innerWidth / 2
    pos.value = { x: snapToLeft ? MARGIN : window.innerWidth - FAB - MARGIN, y: pos.value.y }
  }
}

// 每次打开新游戏时, 把按钮归位到默认位置 (避免上次拖到角落找不到)
watch(
  () => props.url,
  (v, old) => {
    if (v && !old) pos.value = defaultPos()
  }
)
</script>

<style scoped lang="scss">
// 固定像素 (不走 rem): 已在 postcss.config.js exclude 列入 GameIframeOverlay.vue
.game-iframe-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #000;
}

.game-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.exit-fab {
  position: fixed;
  z-index: 10000;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: #e8592a;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: grab;
  touch-action: none; // 关键: 阻止浏览器把拖动当成滚动/缩放手势
  transition: left 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); // 松手吸边的动画, 带一点回弹
  box-shadow:
    0 4px 14px rgba(232, 89, 42, 0.5),
    0 2px 6px rgba(0, 0, 0, 0.3);
  user-select: none;
  -webkit-user-select: none;
  display: grid;
  place-items: center;

  &.dragging {
    cursor: grabbing;
    transition: none;
  }
  &:not(.dragging):active {
    transform: scale(0.92);
  }
}
</style>
